<template>
  <Teleport to="body">
    <div
      @click="closeModal"
      @keydown.escape="closeModal"
      v-if="focusedResume"
      class="modal"
    >
      <div
        ref="modalRef"
        @click.stop
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-preview-title"
        tabindex="-1"
        class="relative w-full max-w-screen-lg pb-16 overflow-hidden rounded-lg bg-jsBlue-0 focus:outline-none"
      >
        <div
          class="flex flex-row items-center justify-between w-full p-6 bg-white"
        >
          <h2
            id="resume-preview-title"
            class="text-xl font-semibold text-jsText-title"
          >
            {{
              focusedResume.fromLinkedIn
                ? 'Created resume from LinkedIn'
                : 'Preview resume'
            }}
          </h2>
          <button
            ref="closeButtonRef"
            type="button"
            @click="closeModal"
            class="flex items-center rounded cursor-pointer hover:bg-jsNeutral-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Close preview modal"
          >
            <FontAwesomeIcon
              class="w-4 h-4 p-2 "
              :icon="['fas', 'xmark']"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="w-full h-full overflow-y-auto max-h-fit innerWrapper">
          <div class="py-4">
            <FontAwesomeIcon
              v-if="focusedResume.isBase"
              :icon="['fas', 'star']"
              class="w-4 h-4 mr-2 cursor-pointer text-jsYellow-60"
              aria-label="Base resume"
            />
            <span class="font-semibold text-xl text-[#172B4D]">{{
              focusedResume.name
            }}</span>
          </div>
          <ResumeViewStatic
            @close="closeModal"
            :showDownload="true"
            :resume="focusedResume"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick } from 'vue'
import ResumeViewStatic from '@/pages/PowerEditNew/components/Content/ResumeViewStatic.vue'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useResumeStore } from '@/store/pinia/resumeStore'
import { useFocusTrap } from '@/composables/useFocusTrap'

export default defineComponent({
  name: 'ResumePreviewModal',
  components: { ResumeViewStatic, FontAwesomeIcon },
  setup() {
    const resumeStore = useResumeStore()
    const { focusedResume } = storeToRefs(resumeStore)
    const modalRef = ref<HTMLElement | null>(null)
    const closeButtonRef = ref<HTMLButtonElement | null>(null)

    const closeModal = () => {
      focusedResume.value = null
    }

    // Handle focus when modal opens/closes
    watch(focusedResume, async (newValue, oldValue) => {
      if (newValue && !oldValue) {
        // Wait for the modal to render, then focus it
        await nextTick()
        await nextTick() // Extra tick for v-if to fully render

        if (modalRef.value) {
          modalRef.value.focus()
        }
      } else if (!newValue && oldValue) {
        // Modal closing - focus on the "Step 1: Select a resume" title
        await nextTick()

        const resumeSelectLabel = document.getElementById('resume-select-label') as HTMLElement
        if (resumeSelectLabel) {
          resumeSelectLabel.focus()
        }
      }
    })

    // Initialize focus trap for Tab key navigation only
    useFocusTrap(modalRef, {
      onEscape: closeModal,
      autoFocus: false, // We manually focus the modal above
    })

    return {
      focusedResume,
      modalRef,
      closeButtonRef,
      closeModal,
    }
  },
})
</script>

<style lang="postcss" scoped>
.modal {
  @apply fixed top-0 left-0 bottom-0 right-0 bg-jsNeutral-100 bg-opacity-70 backdrop-blur-sm pb-4 pt-20 px-4 flex justify-center items-start z-topLayer;
  .innerWrapper {
    @apply px-6 pb-6;
    height: 60vh;
  }
}
</style>
